import React, { useState, useEffect } from 'react';
import { Upload, FileText, Trash2, Eye, X, Plus, Loader } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { vectorKnowledgeService } from '../services/vector-knowledge-service';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Configurar worker do PDF.js (usando worker local)
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface KnowledgeDocument {
  id: string;
  title: string;
  content: string;
  content_type: string;
  size: number;
  created_at: string;
  updated_at: string;
}

interface AssistantKnowledgeBaseProps {
  assistantId: string;
  userId: string;
  tenantId: string;
}

export function AssistantKnowledgeBase({ assistantId, userId, tenantId }: AssistantKnowledgeBaseProps) {
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<KnowledgeDocument | null>(null);
  const toast = useToast();

  useEffect(() => {
    loadDocuments();
  }, [assistantId]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const docs = await vectorKnowledgeService.listDocuments(assistantId);
      setDocuments(docs);
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
      toast.error('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  const extractTextFromPDF = async (file: File): Promise<string> => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ');
        fullText += pageText + '\n\n';
      }

      return fullText.trim();
    } catch (error) {
      console.error('Erro ao extrair texto do PDF:', error);
      throw new Error('Não foi possível extrair o texto do PDF');
    }
  };

  const readFileContent = async (file: File): Promise<string> => {
    const fileType = file.type;
    
    // PDF
    if (fileType === 'application/pdf') {
      return await extractTextFromPDF(file);
    }
    
    // Texto puro, Markdown, etc
    if (fileType.startsWith('text/') || 
        fileType === 'application/json' ||
        file.name.endsWith('.md') ||
        file.name.endsWith('.txt')) {
      return await file.text();
    }
    
    throw new Error('Tipo de arquivo não suportado');
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const file of Array.from(files)) {
      try {
        // Validar tamanho (máx 10MB)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name}: arquivo muito grande (máx 10MB)`);
          errorCount++;
          continue;
        }

        // Extrair conteúdo
        const content = await readFileContent(file);
        
        if (!content || content.length < 10) {
          toast.error(`${file.name}: conteúdo vazio ou muito curto`);
          errorCount++;
          continue;
        }

        // Determinar tipo de conteúdo
        let contentType = 'text';
        if (file.type === 'application/pdf') contentType = 'pdf';
        else if (file.name.endsWith('.md')) contentType = 'markdown';
        else if (file.type === 'text/html') contentType = 'html';

        // Salvar no banco + gerar embeddings
        await vectorKnowledgeService.addDocument(
          assistantId,
          file.name,
          content,
          userId,
          contentType,
          {
            original_filename: file.name,
            file_type: file.type,
            upload_date: new Date().toISOString()
          }
        );

        successCount++;
        toast.info(`${file.name}: gerando embeddings...`);
      } catch (error) {
        console.error(`Erro ao processar ${file.name}:`, error);
        toast.error(`${file.name}: erro ao processar`);
        errorCount++;
      }
    }

    setUploading(false);

    if (successCount > 0) {
      toast.success(`${successCount} documento(s) adicionado(s) com sucesso!`);
      loadDocuments();
    }

    if (errorCount > 0) {
      toast.error(`${errorCount} documento(s) com erro`);
    }

    // Limpar input
    event.target.value = '';
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!confirm('Tem certeza que deseja excluir este documento?')) return;

    try {
      await vectorKnowledgeService.deleteDocument(docId, assistantId);
      toast.success('Documento excluído com sucesso!');
      loadDocuments();
      setSelectedDoc(null);
    } catch (error) {
      console.error('Erro ao excluir documento:', error);
      toast.error('Erro ao excluir documento');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (contentType: string) => {
    switch (contentType) {
      case 'pdf': return '📕';
      case 'markdown': return '📝';
      case 'html': return '🌐';
      default: return '📄';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Carregando documentos...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header com botão de upload */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Base de Conhecimento</h3>
          <p className="text-sm text-gray-600 mt-1">
            Adicione documentos que o agente pode usar como referência nas conversas
          </p>
        </div>
        
        <div>
          <input
            type="file"
            multiple
            accept=".pdf,.txt,.md,.json,text/*"
            onChange={handleFileUpload}
            disabled={uploading}
            className="hidden"
            id="knowledge-file-upload"
          />
          <label htmlFor="knowledge-file-upload" className="cursor-pointer">
            <span
              className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                uploading
                  ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {uploading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Enviando...</span>
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  <span>Adicionar Documentos</span>
                </>
              )}
            </span>
          </label>
        </div>
      </div>

      {/* Informações sobre tipos suportados */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Formatos suportados:</strong> PDF, TXT, Markdown (.md), JSON, HTML
          <br />
          <strong>Tamanho máximo:</strong> 10 MB por arquivo
        </p>
      </div>

      {/* Lista de documentos */}
      {documents.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Nenhum documento adicionado
          </h3>
          <p className="text-gray-600 mb-4">
            Comece adicionando documentos para criar a base de conhecimento deste agente
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">{getFileIcon(doc.content_type)}</span>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate">
                      {doc.title}
                    </h4>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(doc.size)} • {doc.content_type}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between space-x-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedDoc(doc)}
                  className="flex-1"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Ver
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteDocument(doc.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <p className="text-xs text-gray-500 mt-2">
                Adicionado em {new Date(doc.created_at).toLocaleDateString('pt-BR')}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Modal de visualização */}
      {selectedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header do modal */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">{getFileIcon(selectedDoc.content_type)}</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedDoc.title}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {formatFileSize(selectedDoc.size)} • {selectedDoc.content_type}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedDoc(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Conteúdo do documento */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="prose prose-sm max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-gray-700 text-sm leading-relaxed">
                  {selectedDoc.content}
                </pre>
              </div>
            </div>

            {/* Footer do modal */}
            <div className="border-t p-4 flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => handleDeleteDocument(selectedDoc.id)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir
              </Button>
              <Button onClick={() => setSelectedDoc(null)}>
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

